import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { Mutex } from "async-mutex";
import { logout, retrieveUserData, storeRefreshData } from "redux/authSlice";
import { BaseUrl } from "../baseUrl";
import { externalPrepareHeaders, prepareHeaders } from "../defaultHeader";
import { ApiResponse } from "model/response-model";
import { RefreshTokenResponseModel } from "model/auth-model";

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl: BaseUrl,
  prepareHeaders: prepareHeaders, // Pass prepareHeaders directly to fetchBaseQuery
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, prepareHeaders) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, prepareHeaders);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      const model = {
        token: retrieveUserData()?.token,
        refreshToken: retrieveUserData()?.refreshToken,
      };
      try {
        const refreshResult = (await baseQuery(
          {
            url: "Auth/login/refresh-token",
            method: "POST",
            body: model,
          },
          api,
          prepareHeaders
        )) as { data: ApiResponse<RefreshTokenResponseModel> };
        if (refreshResult?.data?.data) {
          api.dispatch(storeRefreshData(refreshResult.data.data));
          result = await baseQuery(args, api, prepareHeaders);
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, prepareHeaders);
    }
  }
  return result;
};

const baseQueryExternal = fetchBaseQuery({
  baseUrl: `${
    process.env.REACT_APP_DOMAIN_BASE || "https://api.pactocoin.com"
  }/admin-svc`,
  prepareHeaders: externalPrepareHeaders, // Pass prepareHeaders directly to fetchBaseQuery
});

export const baseQueryWithReauthExternal: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, prepareHeaders) => {
  await mutex.waitForUnlock();
  let result = await baseQueryExternal(args, api, prepareHeaders);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      const model = {
        token: retrieveUserData()?.token,
        refreshToken: retrieveUserData()?.refreshToken,
      };
      try {
        const refreshResult = (await baseQuery(
          {
            url: "Auth/login/refresh-token",
            method: "POST",
            body: model,
          },
          api,
          prepareHeaders
        )) as { data: ApiResponse<RefreshTokenResponseModel> };
        if (refreshResult?.data?.data) {
          api.dispatch(storeRefreshData(refreshResult.data.data));
          result = await baseQueryExternal(args, api, prepareHeaders);
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQueryExternal(args, api, prepareHeaders);
    }
  }
  return result;
};

const baseQueryTenant = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_TENANT_BASE_URL || `${
    process.env.REACT_APP_DOMAIN_BASE || "https://api.pactocoin.com"
  }/api/`,
  prepareHeaders: externalPrepareHeaders,
});

export const baseQueryWithReauthTenant: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, prepareHeaders) => {
  await mutex.waitForUnlock();
  let result = await baseQueryTenant(args, api, prepareHeaders);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      const model = {
        token: retrieveUserData()?.token,
        refreshToken: retrieveUserData()?.refreshToken,
      };
      try {
        const refreshResult = (await baseQuery(
          {
            url: "Auth/login/refresh-token",
            method: "POST",
            body: model,
          },
          api,
          prepareHeaders
        )) as { data: ApiResponse<RefreshTokenResponseModel> };
        if (refreshResult?.data?.data) {
          api.dispatch(storeRefreshData(refreshResult.data.data));
          result = await baseQueryTenant(args, api, prepareHeaders);
        } else {
          api.dispatch(logout());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await baseQueryTenant(args, api, prepareHeaders);
    }
  }
  return result;
};
